import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import StockNavBar from "../components/StockNavBar";
import { ROTA_NOVA_COMPRA, rotaEditarCompra } from "../features/compras/rotas";
import { Plus, Trash2, ChevronDown, ChevronUp, Edit, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { exportToExcel } from "../lib/utils";
import TablePagination from "../components/TablePagination";
import { CarregandoTabela } from "../components/carregando";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

// URL relativa - funciona em qualquer domínio
const API = '/api';

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// Formata uma data ISO vinda do backend sem conversão de timezone.
// A data-parte (YYYY-MM-DD) já é o dia correto; usar new Date(iso).toLocaleDateString
// desloca meia-noite UTC para o dia anterior em UTC-3. Aqui lemos a data-parte direto.
const formatIsoDateBR = (iso, { withYear = true } = {}) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  return withYear ? `${d}/${m}/${y}` : `${d}/${m}`;
};

// Quantidade com vírgula (12,50), como o resto do painel.
const fmtNum = (v, casas = 2) => (Number(v) || 0).toFixed(casas).replace(".", ",");
// Dinheiro no padrão do painel: 1.234,56
const fmtReais = (v) => (Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// "86,90" / "86.90" / "R$ 86,90" -> "86.90" para comparar com o total do lote.
const valorDigitado = (txt) => {
  const t = String(txt).replace(/[^\d,.]/g, "");
  return t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
};

export default function Purchases() {
  const navigate = useNavigate();
  const [purchaseBatches, setPurchaseBatches] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);

  // Filtros e pesquisa
  const [sortOrder, setSortOrder] = useState("desc"); // desc = mais recente primeiro
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("supplier"); // supplier, value, date
  

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Carregamento com cards (25/10/2026): a lista pisca até a 1ª busca voltar,
  // em vez de abrir dizendo "Nenhuma compra registrada".
  const [primeiraCarga, setPrimeiraCarga] = useState(true);

  useEffect(() => {
    fetchPurchases();
    fetchIngredients();
  }, []);

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/purchases/grouped`, getAuthHeader());
      setPurchaseBatches(response.data);
    } catch (error) {
      toast.error("Erro ao carregar compras");
    } finally {
      setPrimeiraCarga(false);
    }
  };

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`, getAuthHeader());
      // Receitas (is_recipe=true) sao producao interna — nao podem ser compradas.
      // Tambem oculta ingredientes que aparecem como linked_ingredient_id de algum
      // product type=receita (cobre receitas antigas sem flag is_recipe).
      let recipeLinkedIds = new Set();
      try {
        const pRes = await axios.get(`${API}/products`, getAuthHeader());
        recipeLinkedIds = new Set(
          (pRes.data || [])
            .filter(p => p?.product_type === "receita" && p?.linked_ingredient_id)
            .map(p => p.linked_ingredient_id)
        );
        // Fallback por nome quando linked_ingredient_id ta vazio (igual em Estoque)
        const ingByName = new Map();
        for (const i of (response.data || [])) {
          const k = (i.name || "").trim().toLowerCase();
          if (k) ingByName.set(k, i.id);
        }
        for (const p of (pRes.data || [])) {
          if (p?.product_type !== "receita" || p?.linked_ingredient_id) continue;
          const matchId = ingByName.get((p.name || "").trim().toLowerCase());
          if (matchId) recipeLinkedIds.add(matchId);
        }
      } catch {
        // silencioso — se falhar, usa apenas o flag is_recipe
      }
      const onlyPurchasable = (response.data || []).filter(
        i => !i.is_recipe && !recipeLinkedIds.has(i.id) && i.is_active !== false // arquivado nao se compra
      );
      setIngredients(onlyPurchasable);
    } catch (error) {
      toast.error("Erro ao carregar ingredientes");
    }
  };

  const getFilteredAndSortedBatches = () => {
    let filtered = [...purchaseBatches];
    
    // Aplicar pesquisa
    if (searchTerm) {
      filtered = filtered.filter(batch => {
        switch (searchType) {
          case "supplier":
            return (batch.supplier || "").toLowerCase().includes(searchTerm.toLowerCase());
          case "value":
            // O lote vem com `items` e `total_price` (não existe `purchases`):
            // ler batch.purchases derrubava a tela ao buscar por valor.
            return (Number(batch.total_price) || 0).toFixed(2).includes(valorDigitado(searchTerm));
          case "date":
            const batchDate = batch.purchase_date.split("T")[0];
            return batchDate === searchTerm;
          default:
            return true;
        }
      });
    }
    
    // Aplicar ordenação por data
    filtered.sort((a, b) => {
      const dateA = new Date(a.purchase_date);
      const dateB = new Date(b.purchase_date);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  };

  const filteredBatches = useMemo(() => getFilteredAndSortedBatches(), [purchaseBatches, searchTerm, searchType, sortOrder]);
  
  // Lotes paginados
  const paginatedBatches = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredBatches.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBatches, currentPage, itemsPerPage]);

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, searchType, sortOrder]);

  const handleDeleteBatch = async () => {
    if (!batchToDelete) return;

    try {
      await axios.delete(`${API}/purchases/batch/${batchToDelete.id}`, getAuthHeader());
      toast.success("Compra excluída!");
      setDeleteDialogOpen(false);
      setBatchToDelete(null);
      fetchPurchases();
    } catch (error) {
      toast.error("Erro ao excluir compra");
    }
  };

  // Função para alternar status de pagamento de um lote
  const handleTogglePaymentStatus = async (batch) => {
    try {
      const newStatus = !batch.is_paid;
      const payload = {
        is_paid: newStatus,
        due_date: batch.due_date
      };
      
      await axios.patch(
        `${API}/purchases/batch/${batch.batch_id}/payment`, 
        payload, 
        getAuthHeader()
      );
      
      toast.success(newStatus ? "Compra marcada como paga!" : "Compra marcada como pendente!");
      fetchPurchases();
    } catch (error) {
      toast.error("Erro ao atualizar status de pagamento");
      console.error(error);
    }
  };

  const confirmDeleteBatch = (batchId, supplier) => {
    setBatchToDelete({ id: batchId, supplier });
    setDeleteDialogOpen(true);
  };

  const toggleBatch = (batchId) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchId)) {
      newExpanded.delete(batchId);
    } else {
      newExpanded.add(batchId);
    }
    setExpandedBatches(newExpanded);
  };

  const formatDate = (dateStr) => formatIsoDateBR(dateStr);

  return (
    <div className="p-8" data-testid="purchases-page">
      <div className="max-w-7xl mx-auto">
        <StockNavBar />
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Compras
            </h1>
            <p className="text-muted-foreground mt-1">
              Registre compras agrupadas por fornecedor
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                const flatPurchases = [];
                purchaseBatches.forEach(batch => {
                  const itens = batch.items || batch.purchases || [];
                  if (itens.length > 0) {
                    itens.forEach(p => {
                      flatPurchases.push({
                        data: formatIsoDateBR(batch.purchase_date),
                        fornecedor: batch.supplier,
                        ingrediente: p.ingredient_name,
                        quantidade: p.quantity,
                        valor_total: p.price,
                        valor_unitario: p.unit_price
                      });
                    });
                  }
                });
                exportToExcel(flatPurchases, "compras", {
                  data: "Data",
                  fornecedor: "Fornecedor",
                  ingrediente: "Ingrediente",
                  quantidade: "Quantidade",
                  valor_total: "Valor Total",
                  valor_unitario: "Valor Unitário"
                });
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>
            <Button
              data-testid="add-purchase-button"
              onClick={() => navigate(ROTA_NOVA_COMPRA)}
              className="shadow-sm transition-all active:scale-95"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Lançar Compras
            </Button>
          </div>
        </div>

        {/* Filtros e Pesquisa */}
        {purchaseBatches.length > 0 && (
          <div className="bg-card rounded-xl border shadow-sm p-4 mb-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Ordenação */}
              <div className="flex items-center gap-2">
                <Label htmlFor="sort-order" className="text-sm font-medium whitespace-nowrap">
                  Ordenar:
                </Label>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger id="sort-order" className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Mais Recentes</SelectItem>
                    <SelectItem value="asc">Mais Antigas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Tipo de Pesquisa */}
              <div className="flex items-center gap-2">
                <Label htmlFor="search-type" className="text-sm font-medium whitespace-nowrap">
                  Buscar por:
                </Label>
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger id="search-type" className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supplier">Fornecedor</SelectItem>
                    <SelectItem value="value">Valor</SelectItem>
                    <SelectItem value="date">Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Campo de Pesquisa */}
              <div className="flex-1">
                {searchType === "date" ? (
                  <Input
                    type="date"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-10"
                  />
                ) : (
                  <Input
                    placeholder={
                      searchType === "supplier" ? "Digite o nome do fornecedor..." :
                      "Digite o valor..."
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-10"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Grouped Purchases Table */}
        <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
          {primeiraCarga ? (
            <CarregandoTabela linhas={8} colunas={5} className="" />
          ) : purchaseBatches.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nenhuma compra registrada. Clique em "Lançar Compras" para começar.
            </div>
          ) : (
            <>
            <div className="divide-y divide-border">
              {paginatedBatches.map((batch) => {
                const isExpanded = expandedBatches.has(batch.batch_id);
                return (
                  <div key={batch.batch_id} data-testid={`purchase-batch-${batch.batch_id}`}>
                    {/* Batch Summary Row */}
                    <div className="flex items-center px-6 py-4 hover:bg-muted/30 transition-colors">
                      <div 
                        className="flex-1 grid grid-cols-12 gap-4 items-center cursor-pointer"
                        onClick={() => toggleBatch(batch.batch_id)}
                      >
                        <div className="col-span-3 text-muted-foreground">
                          {formatDate(batch.purchase_date)}
                        </div>
                        <div className="col-span-3 font-medium flex items-center gap-2">
                          {batch.supplier || "Sem fornecedor"}
                          {/* Indicador de status de pagamento */}
                          {batch.is_paid === false && (
                            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              A pagar
                              {batch.due_date && ` - ${new Date(batch.due_date + "T00:00:00").toLocaleDateString("pt-BR")}`}
                            </span>
                          )}
                        </div>
                        <div className="col-span-2 text-right font-mono font-semibold text-base tabular-nums">
                          {fmtNum(batch.total_quantity)}
                        </div>
                        <div className="col-span-2 text-right font-mono font-bold text-base tabular-nums">
                          R$ {fmtReais(batch.total_price)}
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-2">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {/* Botão de toggle pagamento */}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePaymentStatus(batch);
                          }}
                          variant="ghost"
                          size="sm"
                          className={batch.is_paid === false ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50" : "text-green-600 hover:text-green-700 hover:bg-green-50"}
                          title={batch.is_paid === false ? "Marcar como paga" : "Marcar como pendente"}
                        >
                          {batch.is_paid === false ? (
                            <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                          ) : (
                            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                          )}
                        </Button>
                        <Button
                          data-testid={`edit-batch-${batch.batch_id}`}
                          onClick={() => navigate(rotaEditarCompra(batch.batch_id))}
                          variant="ghost"
                          size="sm"
                          className="hover:bg-muted"
                        >
                          <Edit className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                          data-testid={`delete-batch-${batch.batch_id}`}
                          onClick={() => confirmDeleteBatch(batch.batch_id, batch.supplier)}
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Items */}
                    {isExpanded && (
                      <div className="bg-muted/50 px-6 py-4">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-muted-foreground uppercase border-b border-border">
                              <th className="pb-2">Item</th>
                              <th className="pb-2 text-right">Quantidade</th>
                              <th className="pb-2 text-right">Preço Unitário</th>
                              <th className="pb-2 text-right">Preço Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(batch.items || []).map((item) => {
                              // Buscar o ingrediente para obter units_per_package e unit_weight
                              const ingredient = ingredients.find(ing => ing.id === item.ingredient_id);
                              const unitsPerPackage = ingredient?.units_per_package || 0;
                              const unitWeight = ingredient?.unit_weight || 0;
                              const ingredientUnit = ingredient?.unit?.toLowerCase() || "";
                              
                              // Se tem units_per_package, calcular quantidade em embalagens
                              let quantityDisplay = item.quantity;
                              let quantityInUnits = null;
                              let unitDisplay = item.ingredient_unit;
                              
                              if (unitsPerPackage > 0 && item.quantity > 0) {
                                // item.quantity está em unidades de VENDA, converter para embalagens
                                quantityDisplay = item.quantity / unitsPerPackage;
                                quantityInUnits = item.quantity;
                                unitDisplay = "emb";
                              } else if (unitWeight > 0 && ingredientUnit === "kg" && item.quantity > 0) {
                                // Para itens em kg com unit_weight, mostrar quantidade em unidades embaixo
                                quantityDisplay = item.quantity;
                                quantityInUnits = item.quantity / unitWeight;
                                unitDisplay = item.ingredient_unit;
                              }
                              
                              return (
                                <tr key={item.id} className="border-b border-border/50">
                                  <td className="py-2">
                                    {item.ingredient_name}
                                  </td>
                                  <td className="py-2 text-right">
                                    <div className="flex flex-col items-end">
                                      <span className="font-mono font-semibold text-sm tabular-nums">{fmtNum(quantityDisplay)} {unitDisplay}</span>
                                      {quantityInUnits !== null && (
                                        <span className="text-xs text-muted-foreground font-sans font-medium mt-0.5 tracking-tight">
                                          {unitsPerPackage > 0 
                                            ? `${fmtNum(quantityInUnits)} ${item.ingredient_unit}`
                                            : `${fmtNum(quantityInUnits)} un`}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-2 text-right font-mono text-muted-foreground text-sm tabular-nums">
                                    R$ {fmtReais(item.unit_price)}/{item.ingredient_unit}
                                  </td>
                                  <td className="py-2 text-right font-mono font-semibold text-sm tabular-nums">
                                    R$ {fmtReais(item.price)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredBatches.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  Nenhuma compra encontrada com os filtros aplicados.
                </div>
              )}
            </div>
            
            {/* Paginação */}
            {filteredBatches.length > 0 && (
              <TablePagination
                currentPage={currentPage}
                totalItems={filteredBatches.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            )}
            </>
          )}
        </div>

        {/* AlertDialog de confirmação de exclusão */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir toda a compra do fornecedor <strong>{batchToDelete?.supplier}</strong>?
                Esta ação não pode ser desfeita e irá remover todos os itens desta compra.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteBatch}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
